import {getTestProps} from '@benchling/js-test-utils';
import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveButton from 'archive/components/buttons/ArchiveButton';
import UnarchiveButton from 'archive/components/buttons/UnarchiveButton';
import isArchived from 'archive/isArchived';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ProcessPlatformFeatureType, ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import ExportDropdown from 'nav/expanded/folder-toolbar/ExportDropdown';
import MoveButton from 'nav/expanded/folder-toolbar/MoveButton';
import {ItemListingQuery} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {openArchiveModalForProcessItems} from 'processes/archive/archiveUtil';
import CopyTasksConfirmationModal from 'processes/modals/CopyTasksConfirmationModal';
import {ProcessManagementItemType} from 'processes/ProcessConstants';
import {getProcessPlatformFeatureConfig} from 'processes/processes-platform/FeatureOptions';
import TagEditorDropdown from 'schemas/tags/tag-editor/TagEditorDropdown';
import {ListableItem} from 'search/SearchConstants';
import AddToStudiesButton from 'studies/studyable/AddToStudiesButton';
import {isItemStudyable} from 'studies/studyable/StudyableUtils';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';

import * as ProcessManagementActions from '../ProcessManagementActions';
import areSelectedTypesExactly, {
  areSelectedTypesSubset,
  areSelectedTypesTaskTypes,
} from './areSelectedTypesExactly';
import ProcessTaskEditorDropdown from './bulk-edit/ProcessTaskEditorDropdown';
import ExecuteTaskGroupsButton from './ExecuteTaskGroupsButton';
import ExecuteTasksButton from './ExecuteTasksButton';
import {FixFailedMappingButton} from './FixFailedMappingButton';
import {getAvailableTypes} from './processes-search-platform/getAvailableTypes';
import {SidepanelProcessItem, doSelectedItemsExceedLimit} from './ProcessManagementSidepanelConstants';
import UndoExecutionButton from './UndoExecutionButton';

type SidepanelProcessTask = ExtractByTypename<ItemListingQuery['processTasks'][number], 'ProcessTaskNode'>;

type SidepanelProcessOutput = ExtractByTypename<
  ItemListingQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

type SidepanelProcess = ExtractByTypename<ItemListingQuery['processes'][number], 'ProcessNode'>;

interface ProcessManagementToolbarBaseProps {
  selectedItems: Array<SidepanelProcessItem>;
  selectedClientOrigin: ProcessClientOrigin;
}

type ProcessManagementToolbarButtonProps = ProcessManagementToolbarBaseProps;

interface ProcessManagementToolbarProps extends ProcessManagementToolbarBaseProps {
  selectedTypes: Array<string>;
}

function ArchiveToolbarButtons(props: ProcessManagementToolbarButtonProps): JSX.Element {
  const hasSelectedOnlyUnarchivable = props.selectedItems.every(
    (item) => isArchived(item) && item.permissions.unarchivable
  );
  const hasSelectedOnlyArchivable = props.selectedItems.every(
    (item) => !isArchived(item) && item.permissions.archivable
  );

  const isAnySelected = props.selectedItems.length > 0;
  let itemType: string | null = isAnySelected ? APIUtil.apiIdToMaybeType(props.selectedItems[0].id) : null;
  if (!props.selectedItems.every((item) => APIUtil.apiIdToMaybeType(item.id) === itemType)) {
    itemType = null;
  }

  const hasSelectedProcessItems =
    itemType != null && areSelectedTypesSubset([itemType], getAvailableTypes(ProcessClientOrigin.PROCESSES));
  const readableType = pluralize(
    props.selectedItems.length,
    itemType != null ? typeToName(itemType) : 'item'
  );
  const {isExceedingLimit, isExceedingLimitMessage} = doSelectedItemsExceedLimit(props.selectedItems);

  if (hasSelectedOnlyUnarchivable) {
    return (
      <>
        {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_UNARCHIVE}) && (
          <UnarchiveButton
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            itemIds={props.selectedItems.map((item) => item.id)}
            onUnarchive={async (ids: Array<string>) => {
              await ArchiveActions.bulkUnarchiveProcessManagement(itemType as ProcessManagementItemType, ids);
            }}
            tooltipTitle={isExceedingLimitMessage ?? `Unarchive ${readableType}`}
            disabled={isExceedingLimit || !isAnySelected || !hasSelectedProcessItems}
          />
        )}
      </>
    );
  }
  return (
    <>
      {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_ARCHIVE}) && (
        <ArchiveButton
          buttonStyle={ButtonStyle.TERTIARY_WHITE}
          disabled={
            isExceedingLimit || !isAnySelected || !hasSelectedOnlyArchivable || !hasSelectedProcessItems
          }
          items={props.selectedItems}
          onClick={(e) => {
            e.stopPropagation();
            openArchiveModalForProcessItems({
              itemType: itemType as ProcessManagementItemType,
              items: props.selectedItems,
            });
          }}
          tooltipTitle={isExceedingLimitMessage ?? `Archive ${readableType}`}
        />
      )}
    </>
  );
}

export function CopyProcessTasksButton(props: ProcessManagementToolbarButtonProps): JSX.Element {
  const processTasks = props.selectedItems as Array<SidepanelProcessTask>;
  const isAnySelected = processTasks.length > 0;
  const {isExceedingLimit, isExceedingLimitMessage} = doSelectedItemsExceedLimit(props.selectedItems);
  const taskIds = processTasks.map(({id}) => id);
  const willRemoveAssigneeFromTask = processTasks.some(
    (processTask) => processTask.assignee != null && processTask.schema.canSetAssigneeOnTaskCreation === false
  );
  const copyTasksText = `Copy ${pluralize(processTasks.length, 'task')}`;

  return (
    <Tooltip title={isExceedingLimitMessage ?? copyTasksText} disabled={!isAnySelected}>
      <Button
        aria-label={copyTasksText}
        disabled={isExceedingLimit || !isAnySelected}
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        onClick={async () => {
          if (willRemoveAssigneeFromTask) {
            await legacyRunModal(CopyTasksConfirmationModal, {taskIds});
            return;
          }
          await ProcessManagementActions.copyTasks(taskIds);
        }}
        icon="copy"
      />
    </Tooltip>
  );
}

function ProcessMoveButton(props: ProcessManagementToolbarButtonProps): JSX.Element {
  const processes = props.selectedItems as Array<SidepanelProcess>;
  const isAnySelected = processes.length > 0;
  const {isExceedingLimit, isExceedingLimitMessage} = doSelectedItemsExceedLimit(props.selectedItems);

  return (
    <MoveButton
      disabled={
        isExceedingLimit || !isAnySelected || !props.selectedItems.every((item) => item.permissions.movable)
      }
      files={props.selectedItems}
      finderId={NavConstants.PROCESSES_FINDER_ID}
      initialSelectedFolderId={processes[0]?.folder.id}
      tooltipTitle={isExceedingLimitMessage ?? 'Move to folder'}
    />
  );
}

function ProcessOutputEditorDropdown(
  props: ProcessManagementToolbarButtonProps & {
    selectedType: ModelTypeConstants.PROCESS_OUTPUT;
    selectedItems: Array<SidepanelProcessOutput>;
  }
): JSX.Element {
  const {isExceedingLimitMessage} = doSelectedItemsExceedLimit(props.selectedItems);

  return (
    <TagEditorDropdown
      results={props.selectedItems.map((item: SidepanelProcessOutput) => ({...item, tags: item.fields}))}
      onSubmit={async (newTag) => {
        await ProcessManagementActions.updateProcessOutputTags(
          props.selectedItems,
          newTag,
          'dashboard',
          ActionOrigin.WORKFLOWS_DASHBOARD,
          ActionMethod.BUTTON_CLICK
        );
      }}
      tooltipTitleWhenSelectable={isExceedingLimitMessage ?? ''}
      buttonStyle={ButtonStyle.TERTIARY_WHITE}
      dropLeft={true}
      actionOrigin={ActionOrigin.WORKFLOWS_EXPANDED_SEARCH_TOOLBAR}
    />
  );
}

interface ProcessManagementEditButtonProps extends ProcessManagementToolbarBaseProps {
  selectedTypes: Array<string>;
}

function ProcessManagementEditButton(props: ProcessManagementEditButtonProps): JSX.Element | null {
  if (areSelectedTypesTaskTypes(props.selectedTypes)) {
    return <ProcessTaskEditorDropdown items={props.selectedItems as Array<SidepanelProcessTask>} />;
  } else if (areSelectedTypesExactly(props.selectedTypes, [ModelTypeConstants.PROCESS_OUTPUT])) {
    return (
      <ProcessOutputEditorDropdown
        {...props}
        selectedItems={props.selectedItems as Array<SidepanelProcessOutput>}
        selectedType={ModelTypeConstants.PROCESS_OUTPUT}
      />
    );
  }
  return null;
}

export default function ProcessManagementToolbar(props: ProcessManagementToolbarProps): JSX.Element {
  function areItemsExecutableAsTasks(): boolean {
    const areSelectedTypesTaskTypes = areSelectedTypesSubset(props.selectedTypes, [
      ModelTypeConstants.PROCESS_TASK,
      ModelTypeConstants.RECIPE_STEP_TASK,
      ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK,
      ModelTypeConstants.ASSAY_STEP_TASK,
      ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK,
    ]);

    // When the user is explicitly viewing "all bioprocessing tasks", we show the execute button even though
    // recipe run tasks are not executable.
    const isSelectingAllBioprocessingTaskTypes = areSelectedTypesExactly(
      props.selectedTypes,
      getAvailableTypes(ProcessClientOrigin.RECIPES)
    );

    if (!areSelectedTypesTaskTypes && !isSelectingAllBioprocessingTaskTypes) {
      return false;
    }

    return isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT});
  }

  function getCsvExportType(): ProcessManagementItemType | null {
    if (areSelectedTypesTaskTypes(props.selectedTypes)) {
      // We allow the export of multiple types as long as they are all task based
      return ModelTypeConstants.PROCESS_TASK;
    } else if (props.selectedTypes.length > 1) {
      return null;
    }
    return props.selectedTypes[0] as ProcessManagementItemType;
  }
  const {isExceedingLimit, isExceedingLimitMessage} = doSelectedItemsExceedLimit(props.selectedItems);
  const isAnySelected = props.selectedItems.length > 0;
  const csvExportType = getCsvExportType();

  return (
    <div
      className="u-flex-display"
      {...getTestProps({component: 'ProcessManagementToolbar', element: 'root'})}
    >
      <div className="buttonGroup">
        {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) && (
          <ProcessManagementEditButton {...props} />
        )}
        {areSelectedTypesExactly(props.selectedTypes, [ModelTypeConstants.PROCESS]) &&
          isFeatureEnabled({feature: ProcessesFeature.PROCESSES_MOVE}) && <ProcessMoveButton {...props} />}
        {areSelectedTypesExactly(props.selectedTypes, [ModelTypeConstants.PROCESS_TASK]) &&
          isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE}) &&
          props.selectedItems.every((item: SidepanelProcessTask) =>
            getProcessPlatformFeatureConfig(
              ProcessPlatformFeatureType.CAN_COPY_TASKS,
              item.schema.executionType,
              item.schema.name
            )
          ) && <CopyProcessTasksButton {...props} />}
        {areSelectedTypesExactly(props.selectedTypes, [ModelTypeConstants.PROCESS_TASK]) &&
          isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) && (
            <UndoExecutionButton tasks={props.selectedItems as Array<SidepanelProcessTask>} />
          )}
        {props.selectedClientOrigin === ProcessClientOrigin.PROCESSES && <ArchiveToolbarButtons {...props} />}
        {areSelectedTypesSubset(props.selectedTypes, [
          ModelTypeConstants.PROCESS_TASK,
          ModelTypeConstants.PROCESS_OUTPUT,
        ]) &&
          isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE}) && (
            <FixFailedMappingButton
              selectedItems={props.selectedItems as Array<SidepanelProcessTask | SidepanelProcessOutput>}
              dataTestAttrs={{
                component: 'ProcessManagementToolbar',
                element: 'fixFailedMappingButton',
              }}
            />
          )}
        {areSelectedTypesSubset(props.selectedTypes, [
          ModelTypeConstants.PROCESS_TASK,
          ...getAvailableTypes(ProcessClientOrigin.RECIPES),
        ]) &&
          BenchlingConfig.STUDIES_ENABLE && (
            <AddToStudiesButton
              itemIds={props.selectedItems.map((item) => item.id)}
              disabled={
                isExceedingLimit ||
                !(isAnySelected && props.selectedItems.every((item) => isItemStudyable(item)))
              }
              tooltipTitleWhenDisabled={isExceedingLimitMessage ?? null}
              buttonStyle={ButtonStyle.TERTIARY_WHITE}
            />
          )}
        {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EXPORT}) && (
          <ExportDropdown
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            disabled={isExceedingLimit || !isAnySelected}
            tooltipTitleWhenDisabled={isExceedingLimitMessage ?? null}
            menuItems={[
              compact([
                csvExportType != null && BenchlingConfig.ENABLE_EXPORTS
                  ? {
                      children: 'Export selection to CSV',
                      onSelect: async () => {
                        await ProcessManagementActions.exportToCSV(
                          csvExportType,
                          props.selectedItems.map(({id}) => id)
                        );
                      },
                      isDisabled: !isAnySelected,
                    }
                  : null,
                {
                  children: EXPORT_AUDIT_LOG_DISPLAY_STRING,
                  onSelect: () => {
                    const selectedIds = props.selectedItems.map((item: ListableItem) => item.id);
                    AuditActions.exportAuditLogs(selectedIds);
                  },
                  isDisabled: !isAnySelected,
                  dataTestAttrs: {component: 'ProcessManagementToolbar', element: 'exportAuditLog'},
                },
              ]),
            ]}
          />
        )}
      </div>
      {areItemsExecutableAsTasks() && (
        <div className={classnames('buttonGroup', css(styles.executeButtonWrapper))}>
          <ExecuteTasksButton
            selectedItems={props.selectedItems}
            actionOrigin={ActionOrigin.SIDEPANEL_UNDETERMINED}
            isSidePanel={true}
          />
        </div>
      )}
      {areSelectedTypesExactly(props.selectedTypes, [ModelTypeConstants.PROCESS]) &&
        isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) && (
          <div className={classnames('buttonGroup', css(styles.executeButtonWrapper))}>
            <ExecuteTaskGroupsButton
              processes={props.selectedItems as Array<SidepanelProcess>}
              actionOrigin={ActionOrigin.SIDEPANEL_UNDETERMINED}
              isSidePanel={true}
            />
          </div>
        )}
    </div>
  );
}

const styles = StyleSheet.create({
  executeButtonWrapper: {
    marginLeft: '8px',
  },
});
